import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CssBaseline, ThemeProvider } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from './store';
import { getCurrentUser } from './store/slices/authSlice';

// Theme
import theme from './theme';

// Layout components
import AppHeader from './components/layout/AppHeader';
import AppSidebar from './components/layout/AppSidebar';

// Pages
import ProjectDashboard from './pages/ProjectDashboard';
import ProjectCreate from './pages/ProjectCreate';
import ProjectDetail from './pages/ProjectDetail';
import ProjectEdit from './pages/ProjectEdit';
import VideoManagement from './pages/VideoManagement';
import Analytics from './pages/Analytics';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import Login from './pages/Login';
import Register from './pages/Register';
import NotFound from './pages/NotFound';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import VideoDetails from './pages/VideoDetails';
import AuthSuccess from './pages/AuthSuccess';
import AuthError from './pages/AuthError';
import Pricing from './pages/Pricing';
import TestPage from './pages/test';
import Workflow from './pages/Workflow';
import RebuildScript from './pages/RebuildScript';
import AnalysisStepReview from './pages/AnalysisStepReview';
import ScriptAnalysis from './pages/ScriptAnalysis';
import UserPreferences from './pages/UserPreferences';
import AdminInstrumentation from './pages/AdminInstrumentation';
import AnalysisInstrumentationPanel from './components/admin/AnalysisInstrumentationPanel';
import ScriptInstrumentationPanel from './components/admin/ScriptInstrumentationPanel';
import StepAnalysisPanel from './components/admin/StepAnalysisPanel';
import MlMetricsPanel from './components/admin/MlMetricsPanel';
import AdminRoute from './components/AdminRoute';
import ReviewerRoute from './components/ReviewerRoute';
import RoleRedirect from './components/RoleRedirect';
import ScorecardsList from './pages/ScorecardsList';
import PublicLandingRoute from './components/PublicLandingRoute';
import Home from './pages/Home';
import EnterpriseAccess from './pages/marketing/EnterpriseAccess';
import ReviewerHome from './pages/reviewer/ReviewerHome';
import ReviewerWorkspace from './pages/reviewer/ReviewerWorkspace';
import ReviewerGuide from './pages/reviewer/ReviewerGuide';

// Landing page component
import Landing from './components/Landing';
import LandingLayout from './layouts/LandingLayout';

// Auth components
import PrivateRoute from './components/PrivateRoute';

// Error Boundary
import ErrorBoundary from './components/ErrorBoundary';

// Dashboard layout component
const DashboardLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppHeader />
      <AppSidebar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: '64px', // AppBar height
          ml: { sm: '240px' }, // Sidebar width on desktop
          width: { sm: `calc(100% - 240px)` }
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

// Main App component
const App: React.FC = () => {
  console.log('[APP] 🚀 App component is mounting/rendering');
  
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  
  console.log('[APP] 🔐 Authentication state:', { isAuthenticated });
  
  // Auth initialization
  useEffect(() => {
    console.log('[APP] ⚡ Auth initialization useEffect running');
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('authToken');
        console.log('[APP] 🎫 Token check:', { hasToken: !!token, tokenLength: token?.length });
        if (token && !isAuthenticated) {
          console.log('[APP] 📞 Dispatching getCurrentUser...');
          await dispatch(getCurrentUser());
          console.log('[APP] ✅ getCurrentUser completed');
        }
      } catch (error) {
        console.log('[APP] ❌ Auth initialization failed:', error);
      }
    };
    initAuth();
  }, [dispatch, isAuthenticated]);
  
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <RoleRedirect />
        <Routes>
          {/* Debug route to test routing */}
          <Route 
            path="/debug" 
            element={
              <div style={{ padding: '20px' }}>
                <h1>Debug Route Working!</h1>
                <p>If you can see this, React Router is working.</p>
                <p>Auth state: {isAuthenticated ? 'Authenticated' : 'Not authenticated'}</p>
                <p>Token exists: {localStorage.getItem('authToken') ? 'Yes' : 'No'}</p>
                <div>
                  <a href="/analytics-simple-test">Test Simple Analytics</a><br/>
                  <a href="/analytics-dashboard-test">Test Analytics with Auth</a><br/>
                  <a href="/analytics-dashboard">Test Full Analytics Dashboard</a>
                </div>
              </div>
            } 
          />
          
          {/* Public routes */}
          <Route 
            path="/" 
            element={<ThemeProvider theme={theme}><CssBaseline /><PublicLandingRoute /></ThemeProvider>} 
          />
          
          <Route 
            path="/home" 
            element={<ThemeProvider theme={theme}><CssBaseline /><PrivateRoute><Home /></PrivateRoute></ThemeProvider>} 
          />
          
          <Route 
            path="/login" 
            element={<ThemeProvider theme={theme}><CssBaseline /><Login /></ThemeProvider>} 
          />
          
          <Route 
            path="/register" 
            element={<ThemeProvider theme={theme}><CssBaseline /><Register /></ThemeProvider>} 
          />
          
          <Route path="/auth/success" element={<ThemeProvider theme={theme}><CssBaseline /><AuthSuccess /></ThemeProvider>} />
          <Route path="/auth/error" element={<ThemeProvider theme={theme}><CssBaseline /><AuthError /></ThemeProvider>} />
          <Route path="/pricing" element={<ThemeProvider theme={theme}><CssBaseline /><Pricing /></ThemeProvider>} />
          <Route path="/enterprise" element={<ThemeProvider theme={theme}><CssBaseline /><EnterpriseAccess /></ThemeProvider>} />
          <Route path="/test" element={<ThemeProvider theme={theme}><CssBaseline /><TestPage /></ThemeProvider>} />
          
          {/* Script Workflow & Analysis Routes */}
          <Route path="/workflow" element={<ThemeProvider theme={theme}><CssBaseline /><PrivateRoute><Workflow /></PrivateRoute></ThemeProvider>} />
          <Route path="/workflow/steps/:sessionId" element={<ThemeProvider theme={theme}><CssBaseline /><PrivateRoute><AnalysisStepReview /></PrivateRoute></ThemeProvider>} />
          <Route path="/workflow/rebuild/:analysisSessionId" element={<ThemeProvider theme={theme}><CssBaseline /><PrivateRoute><RebuildScript /></PrivateRoute></ThemeProvider>} />
          <Route path="/upload" element={<ThemeProvider theme={theme}><CssBaseline /><PrivateRoute><Navigate to="/workflow" replace /></PrivateRoute></ThemeProvider>} />
          <Route path="/analysis/:sessionId" element={<ThemeProvider theme={theme}><CssBaseline /><PrivateRoute><ScriptAnalysis /></PrivateRoute></ThemeProvider>} />
          <Route path="/preferences" element={<ThemeProvider theme={theme}><CssBaseline /><PrivateRoute><UserPreferences /></PrivateRoute></ThemeProvider>} />
          <Route
            path="/admin/instrumentation"
            element={
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <AdminRoute>
                  <AdminInstrumentation />
                </AdminRoute>
              </ThemeProvider>
            }
          >
            <Route index element={<AnalysisInstrumentationPanel />} />
            <Route path="scripts" element={<ScriptInstrumentationPanel />} />
            <Route path="steps" element={<StepAnalysisPanel />} />
            <Route path="ml" element={<MlMetricsPanel />} />
          </Route>
          <Route path="/admin/metrics" element={<ThemeProvider theme={theme}><CssBaseline /><Navigate to="/admin/instrumentation" replace /></ThemeProvider>} />
          <Route path="/admin/script-instrumentation" element={<ThemeProvider theme={theme}><CssBaseline /><Navigate to="/admin/instrumentation/scripts" replace /></ThemeProvider>} />
          <Route path="/scorecards" element={<ThemeProvider theme={theme}><CssBaseline /><PrivateRoute><ScorecardsList /></PrivateRoute></ThemeProvider>} />
          <Route path="/scorecards/:scorecardId" element={<ThemeProvider theme={theme}><CssBaseline /><PrivateRoute><ScriptAnalysis /></PrivateRoute></ThemeProvider>} />

          <Route path="/reviewer" element={<ThemeProvider theme={theme}><CssBaseline /><ReviewerRoute><ReviewerHome /></ReviewerRoute></ThemeProvider>} />
          <Route path="/reviewer/guide" element={<ThemeProvider theme={theme}><CssBaseline /><ReviewerRoute><ReviewerGuide /></ReviewerRoute></ThemeProvider>} />
          <Route path="/reviewer/:scriptId/:model" element={<ThemeProvider theme={theme}><CssBaseline /><ReviewerRoute><ReviewerWorkspace /></ReviewerRoute></ThemeProvider>} />
          
          {/* Test Analytics Dashboard route - NO AUTH REQUIRED */}
          <Route 
            path="/analytics-dashboard-test" 
            element={
              <div style={{ padding: '20px' }}>
                <h1>Analytics Dashboard Test</h1>
                <p>This route is working! Testing analytics component...</p>
                <AnalyticsDashboard />
              </div>
            } 
          />

          {/* Simple Analytics Test - NO AUTH, NO DEPENDENCIES */}
          <Route 
            path="/analytics-simple-test" 
            element={
              <div style={{ padding: '20px' }}>
                <h1>Simple Analytics Test</h1>
                <p>Testing if analytics component loads at all...</p>
                <AnalyticsDashboard />
              </div>
            } 
          />

          {/* Protected routes */}
          <Route 
            path="/dashboard" 
            element={
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <PrivateRoute>
                  <DashboardLayout>
                    <ProjectDashboard />
                  </DashboardLayout>
                </PrivateRoute>
              </ThemeProvider>
            } 
          />

          {/* Projects routes */}
          <Route 
            path="/projects" 
            element={
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <PrivateRoute>
                  <DashboardLayout>
                    <ProjectDashboard />
                  </DashboardLayout>
                </PrivateRoute>
              </ThemeProvider>
            } 
          />
          
          <Route 
            path="/projects/create" 
            element={
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <PrivateRoute>
                  <DashboardLayout>
                    <ProjectCreate />
                  </DashboardLayout>
                </PrivateRoute>
              </ThemeProvider>
            } 
          />
          
          <Route
            path="/projects/:projectId"
            element={
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <PrivateRoute>
                  <ProjectDetail />
                </PrivateRoute>
              </ThemeProvider>
            }
          />
          
          <Route 
            path="/projects/:projectId/edit" 
            element={
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <PrivateRoute>
                  <DashboardLayout>
                    <ProjectEdit />
                  </DashboardLayout>
                </PrivateRoute>
              </ThemeProvider>
            } 
          />
          
          {/* Analytics route */}
          <Route 
            path="/analytics" 
            element={
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <PrivateRoute>
                  <DashboardLayout>
                    <Analytics />
                  </DashboardLayout>
                </PrivateRoute>
              </ThemeProvider>
            } 
          />
          
          {/* Analytics Dashboard route - new fresh route */}
          <Route 
            path="/analytics-dashboard" 
            element={
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <PrivateRoute>
                  <DashboardLayout>
                    <AnalyticsDashboard />
                  </DashboardLayout>
                </PrivateRoute>
              </ThemeProvider>
            } 
          />
          
          {/* Legacy video routes for backward compatibility */}
          <Route path="/videos" element={
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <PrivateRoute>
                <DashboardLayout>
                  <VideoManagement />
                </DashboardLayout>
              </PrivateRoute>
            </ThemeProvider>
          } />
          
          <Route path="/videos/:videoId" element={
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <PrivateRoute>
                <DashboardLayout>
                  <VideoDetails />
                </DashboardLayout>
              </PrivateRoute>
            </ThemeProvider>
          } />
          
          {/* User management routes */}
          <Route path="/profile" element={
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <PrivateRoute>
                <DashboardLayout>
                  <Profile />
                </DashboardLayout>
              </PrivateRoute>
            </ThemeProvider>
          } />
          
          <Route path="/settings" element={
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <PrivateRoute>
                <DashboardLayout>
                  <Settings />
                </DashboardLayout>
              </PrivateRoute>
            </ThemeProvider>
          } />
          
          {/* Catch-all route */}
          <Route 
            path="*" 
            element={<ThemeProvider theme={theme}><CssBaseline /><NotFound /></ThemeProvider>} 
          />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App; 